import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';
import { Loader2 } from 'lucide-react';
import { useCreateCuratedIntegration } from '../../../../api/curated-integrations.api';
import type { CreateCuratedIntegrationDto } from '../../../../api/curated-integrations.types';
import { toast } from 'sonner';

interface CreateIntegrationDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Called when the dialog should be closed. */
  onClose: () => void;
  /** Called after a successful creation so the parent can refresh its list. */
  onCreated: () => void;
}

interface FormState {
  name: string;
  displayName: string;
  description: string;
  category: string;
  mcpUrl: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  displayName: '',
  description: '',
  category: '',
  mcpUrl: '',
};

interface FieldErrors {
  name?: string;
  mcpUrl?: string;
}

/** Validates the form and returns a map of field-level error messages. */
function validate(form: FormState): FieldErrors {
  const errors: FieldErrors = {};

  if (!form.name.trim()) {
    errors.name = 'Name ist erforderlich.';
  } else if (!/^[a-z0-9_-]+$/i.test(form.name.trim())) {
    errors.name = 'Nur Buchstaben, Ziffern, Bindestriche und Unterstriche erlaubt.';
  }

  if (!form.mcpUrl.trim()) {
    errors.mcpUrl = 'MCP-URL ist erforderlich.';
  } else {
    try {
      const url = new URL(form.mcpUrl.trim());
      if (url.protocol !== 'https:') {
        errors.mcpUrl = 'URL muss mit https:// beginnen.';
      }
    } catch {
      errors.mcpUrl = 'Ungültige URL.';
    }
  }

  return errors;
}

/**
 * Modal-Dialog zum Anlegen einer neuen Curated Integration.
 * Felder: name (required), displayName, description, category, mcpUrl (https-Pflicht).
 */
export function CreateIntegrationDialog({
  open,
  onClose,
  onCreated,
}: CreateIntegrationDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const { mutateAsync, isLoading, error } = useCreateCuratedIntegration();

  const handleChange = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear field error on change
    if (fieldErrors[field as keyof FieldErrors]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validate(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    const dto: CreateCuratedIntegrationDto = {
      name: form.name.trim(),
      displayName: form.displayName.trim() || undefined,
      description: form.description.trim() || undefined,
      category: form.category.trim() || undefined,
      mcpUrl: form.mcpUrl.trim(),
    };

    try {
      await mutateAsync(dto);
      toast.success('Integration angelegt.');
      setForm(EMPTY_FORM);
      setFieldErrors({});
      onCreated();
      onClose();
    } catch {
      // error is displayed below via the `error` state from the hook
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setForm(EMPTY_FORM);
      setFieldErrors({});
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Neue Integration anlegen</DialogTitle>
          <DialogDescription>
            Erstellt einen neuen Integration-Entwurf (Draft). Nach dem Anlegen können Sie Icon,
            Methoden und weitere Details konfigurieren.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
          >
            {error.message}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-4 mt-2">
          {/* name */}
          <div className="space-y-1.5">
            <Label htmlFor="ci-name">
              Name <span aria-hidden="true" className="text-red-500">*</span>
            </Label>
            <Input
              id="ci-name"
              placeholder="z.B. salesforce-crm"
              value={form.name}
              onChange={handleChange('name')}
              aria-required="true"
              aria-describedby={fieldErrors.name ? 'ci-name-error' : undefined}
              aria-invalid={!!fieldErrors.name}
            />
            {fieldErrors.name && (
              <p id="ci-name-error" role="alert" className="text-xs text-red-600">
                {fieldErrors.name}
              </p>
            )}
            <p className="text-xs text-gray-400">Technischer Bezeichner (wird in API-Keys verwendet).</p>
          </div>

          {/* displayName */}
          <div className="space-y-1.5">
            <Label htmlFor="ci-display-name">Anzeigename</Label>
            <Input
              id="ci-display-name"
              placeholder="z.B. Salesforce CRM"
              value={form.displayName}
              onChange={handleChange('displayName')}
            />
          </div>

          {/* description */}
          <div className="space-y-1.5">
            <Label htmlFor="ci-description">Beschreibung</Label>
            <Textarea
              id="ci-description"
              placeholder="Kurze Beschreibung der Integration…"
              value={form.description}
              onChange={handleChange('description')}
              rows={2}
            />
          </div>

          {/* category */}
          <div className="space-y-1.5">
            <Label htmlFor="ci-category">Kategorie</Label>
            <Input
              id="ci-category"
              placeholder="z.B. CRM, Produktivität, Kommunikation"
              value={form.category}
              onChange={handleChange('category')}
            />
          </div>

          {/* mcpUrl */}
          <div className="space-y-1.5">
            <Label htmlFor="ci-mcp-url">
              MCP-URL <span aria-hidden="true" className="text-red-500">*</span>
            </Label>
            <Input
              id="ci-mcp-url"
              type="url"
              placeholder="https://mcp.example.com/sse"
              value={form.mcpUrl}
              onChange={handleChange('mcpUrl')}
              aria-required="true"
              aria-describedby={fieldErrors.mcpUrl ? 'ci-mcp-url-error' : undefined}
              aria-invalid={!!fieldErrors.mcpUrl}
            />
            {fieldErrors.mcpUrl && (
              <p id="ci-mcp-url-error" role="alert" className="text-xs text-red-600">
                {fieldErrors.mcpUrl}
              </p>
            )}
            <p className="text-xs text-gray-400">Muss öffentlich erreichbar sein und https:// verwenden.</p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
              Anlegen
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
